import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import { FETCH_STATUS_FAILED, FETCH_STATUS_IDLE, FETCH_STATUS_LOADING, FETCH_STATUS_SUCCEEDED } from "../../utils/constants";

import {Employee, EmployeesState} from "../interfaces";

import {
    fetchEmployees,
    updateEmployee,
    sendQuote,
    completeRegistration,
    terminateContract,
    approveSalaryUpdate,
    rejectSalaryUpdate,
    resetEmployeesUpdateStatus,
    editEorPartner,
    editEorCompany,
    modifyEmployeeSalary,
    deleteExpiredEorEmployee
} from './actions';

const initialState: EmployeesState = {
    status: FETCH_STATUS_IDLE,
    updateStatus: FETCH_STATUS_IDLE,
    error: undefined,
    updateError: undefined,
    employees: [],
};
export const updateEmployeeList = (state: EmployeesState, updatedEmployee: Employee) => {
    state.employees = state.employees.map((employee) =>
        employee._id === updatedEmployee._id ? updatedEmployee : employee
    );
}

export const employeesSlice = createSlice({
    name: 'employees',
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(fetchEmployees.pending, (state) => {
                state.status = FETCH_STATUS_LOADING;
            })
            .addCase(fetchEmployees.fulfilled, (state, action) => {
                state.status = FETCH_STATUS_SUCCEEDED;
                if (action.payload.error) {
                    state.error = action.payload.error;
                    return;
                }
                state.employees = [...action.payload.employees];
            })
            .addCase(fetchEmployees.rejected, (state, action) => {
                state.status = FETCH_STATUS_FAILED;
                state.error =  state.error = (action.payload as any)?.message || action.error.message;
            });

        builder.addCase(resetEmployeesUpdateStatus, (state) => {
            state.updateStatus = FETCH_STATUS_IDLE;
            state.updateError = undefined;
        });

        builder.addMatcher(
            isAnyOf(
                updateEmployee.pending,
                sendQuote.pending,
                completeRegistration.pending,
                terminateContract.pending,
                approveSalaryUpdate.pending,
                rejectSalaryUpdate.pending,
                editEorPartner.pending,
                editEorCompany.pending,
                modifyEmployeeSalary.pending,
                deleteExpiredEorEmployee.pending
            ),
            (state) => {
                state.updateStatus = FETCH_STATUS_LOADING;
            }
        );

        builder.addMatcher(
            isAnyOf(
                updateEmployee.fulfilled,
                sendQuote.fulfilled,
                completeRegistration.fulfilled,
                terminateContract.fulfilled,
                approveSalaryUpdate.fulfilled,
                rejectSalaryUpdate.fulfilled,
                editEorCompany.fulfilled,
                editEorPartner.fulfilled,
                modifyEmployeeSalary.fulfilled,
                deleteExpiredEorEmployee.fulfilled
            ),
            (state, action) => {
                state.updateStatus = FETCH_STATUS_SUCCEEDED;
                if (action.payload?.employee) {
                    updateEmployeeList(state, action.payload.employee);
                } else if (action.type === 'delete_expired_eor_employee/fulfilled') {
                    const { employeeId } = action.meta.arg;
                    state.employees = state.employees.filter(emp => emp._id !== employeeId);
                }
            }
        );

        // Handling rejected state for multiple actions
        builder.addMatcher(
            isAnyOf(
                updateEmployee.rejected,
                sendQuote.rejected,
                completeRegistration.rejected,
                terminateContract.rejected,
                approveSalaryUpdate.rejected,
                rejectSalaryUpdate.rejected,
                editEorPartner.rejected,
                editEorCompany.rejected,
                modifyEmployeeSalary.rejected,
                deleteExpiredEorEmployee.rejected
            ),
            (state, action) => {
                state.updateStatus = FETCH_STATUS_FAILED;
                state.updateError = (action.payload as any)?.error || action.error.message;
            }
        );
    },
});

export default employeesSlice.reducer;
